import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { IconButton } from 'react-native-paper';
import { Audio } from 'expo-av';
import * as FileSystem from 'expo-file-system';
import { theme } from '../../screens/Common.style';

let currentSound = null;
let setCurrentStopper = null;

export default function GameVoiceMessageBubble({ audioUrl, duration, senderName, mine, bubbleColor }) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const soundRef = useRef(null);

  useEffect(() => {
    return () => {
      if (soundRef.current) {
        soundRef.current.unloadAsync();
      }
    };
  }, []);

  const stopCurrent = async () => {
    if (currentSound && currentSound !== soundRef.current) {
      await currentSound.stopAsync();
      if (setCurrentStopper) setCurrentStopper(false);
    }
  };

  const handlePlay = async () => {
    if (playing) {
      if (currentSound === soundRef.current) {
        currentSound = null;
        setCurrentStopper = null;
      }
      await soundRef.current?.unloadAsync();
      soundRef.current = null;
      setPlaying(false);
      setProgress(0);
      return;
    }

    await stopCurrent();

    try {
      if (Platform.OS !== 'web') {
        await Audio.setAudioModeAsync({ allowsRecordingIOS: false, playsInSilentModeIOS: true });
      }

      // Always unload before recreating to avoid stale sound state
      if (soundRef.current) {
        if (currentSound === soundRef.current) {
          currentSound = null;
          setCurrentStopper = null;
        }
        await soundRef.current.unloadAsync();
        soundRef.current = null;
      }

      let uri = audioUrl;
      if (audioUrl?.startsWith('data:') && Platform.OS !== 'web') {
        const base64 = audioUrl.split(',')[1];
        const tmpPath = `${FileSystem.cacheDirectory}voice_${Date.now()}.m4a`;
        await FileSystem.writeAsStringAsync(tmpPath, base64, {
          encoding: FileSystem.EncodingType.Base64,
        });
        uri = tmpPath;
      }

      const { sound } = await Audio.Sound.createAsync(
        { uri },
        { shouldPlay: true },
        (status) => {
          if (status.isLoaded) {
            if (status.durationMillis) {
              setProgress(status.positionMillis / status.durationMillis);
            }
            if (status.didJustFinish) {
              setPlaying(false);
              setProgress(0);
              currentSound = null;
              setCurrentStopper = null;
            }
          }
        },
      );
      soundRef.current = sound;
      currentSound = sound;
      setCurrentStopper = setPlaying;
      setPlaying(true);
    } catch (e) {
      console.warn('[VoiceBubble] playback failed', e);
    }
  };

  const durationLabel = duration
    ? `${Math.floor(duration / 60)}:${(duration % 60).toString().padStart(2, '0')}`
    : '0:00';

  const bgStyle = mine
    ? styles.bubbleMine
    : [styles.bubbleTheirs, bubbleColor && { backgroundColor: bubbleColor }];

  return (
    <View style={[styles.bubble, bgStyle]}>
      {!mine && <Text style={styles.sender}>{senderName}</Text>}
      <View style={styles.content}>
        <TouchableOpacity onPress={handlePlay}>
          <IconButton
            icon={playing ? 'pause' : 'play'}
            size={22}
            iconColor={mine ? '#fff' : theme.colors.primary}
          />
        </TouchableOpacity>
        <View style={styles.waveformContainer}>
          <View style={[styles.waveformTrack, !mine && styles.waveformTrackTheirs]}>
            <View style={[
              styles.waveformFill,
              !mine && styles.waveformFillTheirs,
              { width: `${progress * 100}%` },
            ]} />
          </View>
        </View>
        <Text style={[styles.duration, mine && styles.durationMine]}>{durationLabel}</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  bubble: { maxWidth: '75%', borderRadius: 18, padding: 4, marginVertical: 3 },
  bubbleMine: { backgroundColor: theme.colors.primary, alignSelf: 'flex-end' },
  bubbleTheirs: { backgroundColor: '#f0f0f0', alignSelf: 'flex-start' },
  sender: { fontSize: 11, color: '#888', marginLeft: 8, marginBottom: 2 },
  content: { flexDirection: 'row', alignItems: 'center', paddingRight: 8 },
  waveformContainer: { flex: 1, marginHorizontal: 4 },
  waveformTrack: { height: 4, backgroundColor: 'rgba(0,0,0,0.12)', borderRadius: 2, overflow: 'hidden' },
  waveformTrackTheirs: { backgroundColor: 'rgba(0,0,0,0.15)' },
  waveformFill: { height: '100%', backgroundColor: 'rgba(255,255,255,0.8)', borderRadius: 2 },
  waveformFillTheirs: { backgroundColor: 'rgba(0,0,0,0.35)' },
  duration: { fontSize: 12, color: '#666', minWidth: 28, textAlign: 'right' },
  durationMine: { color: 'rgba(255,255,255,0.8)' },
});
